import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { APP_CONTAINER_ROUTES, APP_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams, AppStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { CreditMemoDetailComponent } from '@/screens/credit-memo-detail/credit-memo-detail-component';
import { formatDate } from '@/utils/date';
import { gql } from '@apollo/client';
import { InvoiceDetailComponent } from '@/screens/invoice-detail/invoice-detail-component';
import { isNotMaliPhoneNumber } from '@/utils/phone-number';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { OfflineComponent } from '@/components/offline-component';
import { openPDF } from '@/utils/navigation';
import { PageContainer } from '@/components/page-container';
import { PaymentBatchLineItemName, PaymentBatchStatus } from '@/enums';
import { PaymentHistoryDetailItemOption } from './payment-history-detail-list-item';
import { PaymentHistoryDetailList } from './payment-history-detail-list';
import {
  PaymentHistoryDetailListItemFieldsFragment,
  Payments_GetPaymentBatchQuery,
  Payments_GetPaymentBatchQueryVariables,
} from '@/types';
import { PaymentStatusCard } from './payment-status-card';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, Text, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_PAYMENT_BATCH_QUERY = gql`
  fragment PaymentHistoryDetailListItemFields on payment_batch_line_items {
    amount_in_micros
    created_at
    credit_memo {
      credit_number
      uuid
    }
    name
    order_invoice {
      invoice_number
      uuid
    }
    type
    uuid
  }

  fragment PaymentHistoryDetailFields on payment_batches {
    created_at
    created_by {
      name
      supplier_employee_overrides(
        where: {
          supplier: {
            supplier_payment_batches: { uuid: { _eq: $paymentBatchUUID } }
          }
        }
      ) {
        name
        uuid
      }
      uuid
    }
    customer_facing_id
    document_url
    link_paid_at
    memo
    pay_by_link_recipient {
      email
      employee {
        name
        supplier_employee_overrides(
          where: {
            supplier: {
              supplier_payment_batches: { uuid: { _eq: $paymentBatchUUID } }
            }
          }
        ) {
          name
          uuid
        }
        uuid
      }
      phone_number
      uuid
    }
    payment_batch_amount {
      charged_amount_in_micros
      payment_batch_uuid
    }
    payment_batch_line_items {
      ...PaymentHistoryDetailListItemFields
    }
    payment_method {
      display_text
      uuid
    }
    payment_method_display_text
    status
    uuid
  }

  query Payments_GetPaymentBatch($paymentBatchUUID: uuid!) {
    payment_batches_by_pk(uuid: $paymentBatchUUID) {
      ...PaymentHistoryDetailFields
    }
  }
`;

interface Props {
  paymentBatchUUID: string;
}

type NavigationType = CompositeNavigationProp<
  StackNavigationProp<AppStackParams, APP_ROUTES>,
  StackNavigationProp<AppContainerStackParams>
>;

export const PaymentDetailComponent: React.FC<Props> = ({
  paymentBatchUUID,
}) => {
  const { navigate } = useNavigation<NavigationType>();
  const { bottom } = useSafeAreaInsets();
  const { currencyCode } = usePaymentsContext();
  const { isSmallLayout } = useLayoutContext();
  const isOfflineModeActive = useOfflineModeActive();
  const {
    selectedRestaurantRelationship: { transactionFeeName },
  } = useUserContext();

  const { data, loading, hasResponse } = useWrappedQuery<
    Payments_GetPaymentBatchQuery,
    Payments_GetPaymentBatchQueryVariables
  >(GET_PAYMENT_BATCH_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      paymentBatchUUID,
    },
  });

  const [selectedListItem, setSelectedListItem] =
    useState<PaymentHistoryDetailListItemFieldsFragment | null>(null);

  const paymentBatch = data?.payment_batches_by_pk;

  const navigationHeaderProps: NavigationHeaderContentProps = useMemo(() => {
    if (loading || !paymentBatch) {
      return {
        showBackButton: isSmallLayout,
        showShadow: false,
      };
    }

    const payByLinkRecipientPhoneNumber = isNotMaliPhoneNumber(
      paymentBatch.pay_by_link_recipient?.phone_number,
    )
      ? paymentBatch.pay_by_link_recipient?.phone_number
      : undefined;
    const paidByName = paymentBatch.link_paid_at
      ? paymentBatch.pay_by_link_recipient?.employee
          ?.supplier_employee_overrides[0]?.name ||
        paymentBatch.pay_by_link_recipient?.employee?.name ||
        paymentBatch.pay_by_link_recipient?.email ||
        payByLinkRecipientPhoneNumber
      : (paymentBatch.created_by?.supplier_employee_overrides[0]?.name ??
        paymentBatch.created_by?.name);
    const paymentMethod =
      paymentBatch.payment_method_display_text ||
      paymentBatch.payment_method?.display_text ||
      '';

    const paymentStatusComponent = (
      <View style={styles.m4}>
        <PaymentStatusCard
          createdAt={new Date(paymentBatch.created_at)}
          status={paymentBatch.status as PaymentBatchStatus}
        />
      </View>
    );

    const paidDateComponent = (
      <Text style={TYPOGRAPHY.h2}>
        {i18n.t('payment_paid_date', {
          date: formatDate(
            paymentBatch.link_paid_at || paymentBatch.created_at,
          ),
        })}
      </Text>
    );

    const paidByComponent =
      paidByName && paymentMethod ? (
        <Text
          ellipsizeMode="middle"
          numberOfLines={1}
          style={[styles.m4, TYPOGRAPHY.listSubtitle]}
        >
          {i18n.t('payment_history_paid_by_method', {
            name: paidByName,
            paymentMethod,
          })}
        </Text>
      ) : null;

    if (isSmallLayout) {
      return {
        centerTitle: true,
        contentFooter: (
          <View style={styles.navigationHeaderBase}>
            {paidDateComponent}
            <View style={styles.headerTextContainer}>
              {paymentStatusComponent}
              {paidByComponent}
            </View>
          </View>
        ),
        showBackButton: true,
        title: i18n.t('payment_title', {
          customerFacingId: paymentBatch.customer_facing_id,
        }),
      };
    }

    return {
      centerTitle: false,
      contentContainerStyle: styles.largeContentContainerStyle,
      contentStyle: styles.largeContent,
      showBackButton: false,
      title: (
        <View style={styles.titleLarge}>
          <View style={styles.titleLargeHeader}>
            <View collapsable={false}>
              <Text style={[TYPOGRAPHY.h3, styles.titleLargeText]}>
                {i18n.t('payment_title', {
                  customerFacingId: paymentBatch.customer_facing_id,
                })}
              </Text>
              <View style={styles.paidDateMarginLarge}>
                {paidDateComponent}
              </View>
            </View>
            {paymentBatch?.document_url ? (
              <Button
                onPress={() => openPDF(paymentBatch.document_url!)}
                trackingData={{
                  name: 'payments.view_receipt_button',
                  properties: {
                    paymentBatchUUID: paymentBatch.uuid,
                  },
                }}
                type="secondary"
              >
                {i18n.t('view_receipt_pdf')}
              </Button>
            ) : null}
          </View>
          {paymentStatusComponent}
          {paidByComponent}
        </View>
      ),
    };
  }, [isSmallLayout, loading, paymentBatch]);

  const onGoToInvoice = (invoiceUUID: string) => {
    navigate(APP_CONTAINER_ROUTES.InvoiceDetail, {
      invoiceUUID,
      renderPayable: false,
    });
  };

  const onGoToCreditMemo = (creditMemoUUID: string) => {
    navigate(APP_CONTAINER_ROUTES.CreditMemoDetail, {
      creditMemoUUID,
    });
  };

  const sections = useMemo(() => {
    return [
      {
        data: (paymentBatch?.payment_batch_line_items || [])
          .reduce<PaymentHistoryDetailItemOption[]>((acc, pbli) => {
            if (pbli.order_invoice) {
              acc.push({
                metadata: {
                  paymentBatchLineItem: pbli,
                },
                title: pbli.order_invoice.invoice_number || pbli.uuid,
                value: pbli.uuid,
              });
            } else if (pbli.credit_memo) {
              acc.push({
                metadata: {
                  paymentBatchLineItem: pbli,
                },
                title: pbli.credit_memo.credit_number || i18n.t('credit'),
                value: pbli.uuid,
              });
            } else if (pbli.name === PaymentBatchLineItemName.REFUND) {
              acc.push({
                metadata: {
                  paymentBatchLineItem: pbli,
                },
                title: i18n.t('refund_date', {
                  date: formatDate(pbli.created_at) || '',
                }),
                value: pbli.uuid,
              });
            }

            return acc;
          }, [])
          // Sort line items by invoice -> credit memo -> refund
          // by giving each item a weight according to its type
          .sort((a, b) => {
            const aItem = a.metadata?.paymentBatchLineItem;
            const aWeight = aItem?.order_invoice
              ? -3
              : aItem?.credit_memo
                ? -2
                : -1;

            const bItem = b.metadata?.paymentBatchLineItem;
            const bWeight = bItem?.order_invoice
              ? 3
              : bItem?.credit_memo
                ? 2
                : 1;

            return aWeight + bWeight;
          }),
        key: null,
      },
    ];
  }, [paymentBatch?.payment_batch_line_items]);

  const transactionFeeLineItem = paymentBatch?.payment_batch_line_items.find(
    (pbli) => pbli.name === PaymentBatchLineItemName.TRANSACTION_FEE,
  );

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={navigationHeaderProps}
    >
      {(scrollViewProps) =>
        loading ? (
          <View
            style={[
              scrollViewProps.contentContainerStyle,
              styles.staticContentContainer,
            ]}
          >
            <ActivityIndicator />
          </View>
        ) : !hasResponse && isOfflineModeActive ? (
          <View
            style={[
              scrollViewProps.contentContainerStyle,
              styles.staticContentContainer,
            ]}
          >
            <OfflineComponent />
          </View>
        ) : (
          <View style={styles.base}>
            {selectedListItem?.order_invoice?.uuid ? (
              <BaseActionSheet
                contentStyle={styles.orderActionSheetContent}
                isVisible={Boolean(selectedListItem)}
                modalStyle={styles.orderActionSheetModal}
                onBackButtonPress={() => setSelectedListItem(null)}
                onClose={() => setSelectedListItem(null)}
                title={i18n.t('invoice_number', {
                  number: selectedListItem?.order_invoice?.invoice_number,
                })}
              >
                <InvoiceDetailComponent
                  invoiceUUID={selectedListItem.order_invoice.uuid}
                  renderPayable={false}
                />
              </BaseActionSheet>
            ) : selectedListItem?.credit_memo?.uuid ? (
              <BaseActionSheet
                contentStyle={styles.orderActionSheetContent}
                isVisible={Boolean(selectedListItem)}
                modalStyle={styles.orderActionSheetModal}
                onBackButtonPress={() => setSelectedListItem(null)}
                onClose={() => setSelectedListItem(null)}
                title={i18n.t('credit_number', {
                  number: selectedListItem?.credit_memo?.credit_number,
                })}
              >
                <CreditMemoDetailComponent
                  creditMemoUUID={selectedListItem.credit_memo.uuid}
                />
              </BaseActionSheet>
            ) : null}
            <PaymentHistoryDetailList
              currencyCode={currencyCode}
              paymentMemo={paymentBatch?.memo}
              paymentTotal={
                paymentBatch?.payment_batch_amount?.charged_amount_in_micros ||
                0
              }
              scrollViewProps={scrollViewProps}
              sections={sections}
              setSelectedListItem={(item) => {
                if (isSmallLayout) {
                  if (item.order_invoice?.uuid) {
                    onGoToInvoice(item.order_invoice?.uuid);
                  } else if (item.credit_memo?.uuid) {
                    onGoToCreditMemo(item.credit_memo.uuid);
                  }
                } else {
                  setSelectedListItem(item);
                }
              }}
              transactionFee={transactionFeeLineItem?.amount_in_micros || null}
              transactionFeeName={transactionFeeName}
            />
            {isSmallLayout && paymentBatch?.document_url ? (
              <Button
                containerStyle={[
                  styles.buttonContainer,
                  { marginBottom: Math.max(bottom, SCREEN_PADDING) },
                ]}
                onPress={() => openPDF(paymentBatch.document_url!)}
                trackingData={{
                  name: 'payments.view_receipt_button',
                  properties: {
                    paymentBatchUUID: paymentBatch.uuid,
                  },
                }}
              >
                {i18n.t('view_receipt_pdf')}
              </Button>
            ) : null}
          </View>
        )
      }
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
  },
  buttonContainer: {
    marginHorizontal: SCREEN_PADDING,
    marginTop: 16,
  },
  headerTextContainer: {
    marginTop: 4,
  },
  largeContent: {
    height: 'auto',
  },
  largeContentContainerStyle: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
    paddingTop: SCREEN_PADDING_LARGE,
  },
  m4: {
    marginVertical: 4,
  },
  navigationHeaderBase: {
    paddingBottom: 16,
  },
  orderActionSheetContent: {
    flex: 1,
    paddingHorizontal: 0,
  },
  orderActionSheetModal: {
    flex: 1,
    maxWidth: 820,
  },
  paidDateMarginLarge: {
    marginTop: 8,
  },
  staticContentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  titleLarge: {
    flex: 1,
  },
  titleLargeHeader: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  titleLargeText: {
    lineHeight: 20,
  },
});
